import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Edit3, BookOpen, Layers, ShieldCheck, Sun, Moon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth, useTheme } from "../../../global-components/src";
import { getImageUser, getImageUserNotFound, hasPosition } from "../../../global-components/src/utils/auth";
import "./blog-navbar.css";

interface BlogNavbarProps {
    search?: string;
    onSearchChange?: (val: string) => void;
    showSearch?: boolean;
}

const BlogNavbar: React.FC<BlogNavbarProps> = ({ search = "", onSearchChange, showSearch = false }) => {
    const { t } = useTranslation();
    const { user, isAuthenticated } = useAuth();
    const { isDarkMode, toggleDarkMode } = useTheme();
    const navigate = useNavigate();

    const canModerate = Boolean(
        hasPosition(user) || user?.permissions?.includes("approve_blog_post")
    );

    return (
        <header className="blog-navbar-wrapper">
            <div className="blog-navbar-container">
                <div className="blog-navbar-left">
                    <Link to="/blog" className="blog-logo-link">
                        <img src="/img/pacoca-sem-braco-rounded.png" alt="Paçoca" className="blog-logo-icon" />
                        <span className="blog-logo-text">{t("blog.title")}</span>
                    </Link>

                    <div className="blog-nav-links d-none d-md-flex">
                        <Link to="/blog" className="blog-nav-item">
                            <BookOpen size={16} />
                            <span>{t("blog.home")}</span>
                        </Link>
                        {isAuthenticated && (
                            <Link to="/blog/gerenciar" className="blog-nav-item">
                                <Layers size={16} />
                                <span>{t("blog.myArticles")}</span>
                            </Link>
                        )}
                        {canModerate && (
                            <Link to="/blog/gerenciar?tab=moderation" className="blog-nav-item blog-nav-mod">
                                <ShieldCheck size={16} />
                                <span>{t("blog.moderation")}</span>
                            </Link>
                        )}
                    </div>
                </div>

                {showSearch && onSearchChange && (
                    <div className="blog-navbar-search d-none d-sm-block">
                        <input
                            type="text"
                            placeholder={t("blog.searchPlaceholder")}
                            value={search}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="blog-search-input"
                        />
                    </div>
                )}

                <div className="blog-navbar-right">
                    <button
                        onClick={toggleDarkMode}
                        className="blog-theme-btn"
                        title={isDarkMode ? "Tema Claro" : "Tema Escuro"}
                        type="button"
                    >
                        {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                    </button>

                    {isAuthenticated ? (
                        <>
                            <button
                                onClick={() => navigate("/blog/escrever")}
                                className="blog-write-btn"
                                type="button"
                            >
                                <Edit3 size={16} />
                                <span className="d-none d-sm-inline">{t("blog.writeArticle")}</span>
                            </button>

                            <Link to={`/@${user?.user_name}`} className="blog-user-avatar-link">
                                <img
                                    src={getImageUser(user?.img_account, undefined, user?.name)}
                                    onError={(e) => { e.currentTarget.src = getImageUserNotFound(user?.name); }}
                                    alt={user?.name || "Usuário"}
                                    className="blog-user-avatar"
                                />
                            </Link>
                        </>
                    ) : (
                        <button
                            onClick={() => navigate("/login")}
                            className="blog-login-btn"
                            type="button"
                        >
                            {t("generics.login")}
                        </button>
                    )}
                </div>
            </div>
        </header>
    );
};

export default BlogNavbar;
